/**
 * Single simulation tick frame — extracted from App.tsx (Phase 1 useGameLoop).
 * Reads/writes gameStore; App supplies narrative/policy callbacks via deps.
 */
import { BuildingType } from '../../../../types';
import type {
  NewsItem,
  Policy,
  PolicyChallenge,
  PolicyGameEffect,
  RandomEvent,
  Scenario,
  ScenarioObjective,
} from '../../../../types';
import { WIN_BUDGET } from '../../../../constants';
import { resolvePlayAudio } from '../hooks/useAudio';
import { applyActiveLayerTickBonus } from '../../../../utils/undergroundTick';
import { getLayerManager } from '../../../../services/layerManagerInstance';
import { architectureFeatures } from '../../../infrastructure/config/features';
import { resolveTickMetrics } from '../hooks/useGameTick';
import { nextNewsId } from './newsId';
import { executeEnforcePolicies, executeLegacyPolicyEnforcement } from '../hooks/useEnforcePolicies';
import { executeAdvanceResearch, executeLegacyAdvanceResearch } from '../hooks/useAdvanceResearch';
import { executeOrgSimulationUpdate } from '../hooks/useOrgSimulation';
import {
  executeScenarioObjectives,
  executeLegacyScenarioObjectives,
  executeMilestoneCheck,
  executeLegacyMilestoneCheck,
} from '../hooks/useTrainingEvaluation';
import { recordPlatformEventAuto } from '../hooks/usePlatformPersistence';
import { resolveTriggerScriptedEvent } from '../hooks/useScriptedEvents';
import { resolveTriggerRandomEvent } from '../hooks/useRandomEvents';
import { resolveEvaluateThreat } from '../hooks/useThreatEvaluation';
import { gameStore } from '../../../../stores/gameStore';
import {
  formatPolicyEffectNews,
  mergePolicyIntoTickFeedback,
  sumPolicyTickEffects,
} from './policyTickFeedback';

export type TickFrameDeps = {
  activeScenario: Scenario | null;
  activeEvent: RandomEvent | null;
  addNewsItem: (item: NewsItem) => void;
  setPolicies: (p: Policy[]) => void;
  setPolicyChallenges: (c: PolicyChallenge[]) => void;
  setPolicyEffects: (e: PolicyGameEffect[]) => void;
  setActiveEvent: (e: RandomEvent | null) => void;
  setScenarioObjectives: (
    fn: (prev: ScenarioObjective[]) => ScenarioObjective[]
  ) => void;
  fetchNews: () => void;
};

export function runTickFrame(deps: TickFrameDeps): void {
  const store = gameStore.getState();
  const nextTick = store.tickNumber + 1;
  gameStore.setState({ tickNumber: nextTick });

  const currentStats = gameStore.getState().stats;
  const currentResearch = gameStore.getState().research;
  const currentGrid = gameStore.getState().grid;
  /** Tick-start snapshot — objectives/milestones match legacy setInterval semantics */
  const tickStartStats = currentStats;
  const tickStartResearch = currentResearch;

  if (currentResearch.activeResearchId) {
    const researchResult = architectureFeatures.useAdvanceResearchUseCase
      ? executeAdvanceResearch(currentResearch)
      : executeLegacyAdvanceResearch(currentResearch);

    gameStore.getState().setResearch(researchResult.research);

    if (researchResult.completedTechId && researchResult.newsMessage) {
      deps.addNewsItem({
        id: nextNewsId(),
        text: researchResult.newsMessage,
        type: 'positive',
      });
      resolvePlayAudio({ type: 'sfx', sound: 'success' });
    }
  }

  const policyResult = architectureFeatures.usePolicyUseCases
    ? executeEnforcePolicies()
    : executeLegacyPolicyEnforcement();
  deps.setPolicies(policyResult.policies);
  deps.setPolicyChallenges(policyResult.challenges);
  deps.setPolicyEffects(policyResult.policyEffects);
  const currentPolicyEffects = policyResult.policyEffects;

  if (architectureFeatures.useOrgSimulationUseCase) {
    executeOrgSimulationUpdate(nextTick);
  }

  let tickMetrics = resolveTickMetrics({
    grid: currentGrid,
    stats: currentStats,
    research: currentResearch,
    tickNumber: nextTick,
  });

  tickMetrics = applyActiveLayerTickBonus(
    tickMetrics,
    getLayerManager().getCurrentState().currentLayer
  );

  const {
    netBudget,
    netComp,
    dailyThreat,
    deltas: newDeltas,
    buildingCounts,
    revenueHalted,
    violations,
    violationTiles: vTiles,
    breakdown,
    grid: updatedGrid,
  } = tickMetrics;

  const policyTotals = sumPolicyTickEffects(currentPolicyEffects);
  const tickFeedback = mergePolicyIntoTickFeedback(breakdown, newDeltas, policyTotals);

  gameStore.setState({
    grid: updatedGrid,
    deltas: tickFeedback.deltas,
    violationTiles: vTiles,
    simBreakdown: tickFeedback.breakdown,
  });

  const announcedPolicyIds = gameStore.getState().policyEffectNewsIds;
  const newlyActivePolicies = currentPolicyEffects.filter(
    (effect) => !announcedPolicyIds.includes(effect.policyId),
  );
  if (newlyActivePolicies.length > 0) {
    newlyActivePolicies.forEach((effect) => {
      const policy = policyResult.policies.find((p) => p.id === effect.policyId);
      deps.addNewsItem({
        id: nextNewsId(),
        text: formatPolicyEffectNews(effect, policy),
        type:
          effect.effects.compliance > 0 || effect.effects.threat < 0
            ? 'positive'
            : 'neutral',
      });
    });
    gameStore.setState({
      policyEffectNewsIds: [
        ...announcedPolicyIds,
        ...newlyActivePolicies.map((effect) => effect.policyId),
      ],
    });
  }

  violations.forEach((v) =>
    deps.addNewsItem({ id: nextNewsId(), text: v, type: 'negative' })
  );
  if (revenueHalted) {
    deps.addNewsItem({
      id: nextNewsId(),
      text: 'Revenue Halted: Missing Vault',
      type: 'negative',
    });
  }

  if (netBudget < -50 && nextTick % 3 === 0 && currentStats.budget < 800) {
    const alerts = [
      '🚨 CRITICAL: Losing money fast! Research new buildings (Press R → Research)',
      "💡 URGENT: Need Treasury for revenue? Research 'Deep Operations' (Press R → 4)",
      "⚠️ WARNING: Can't afford buildings? Take a loan or launch more products!",
      "🔥 EMERGENCY: Research 'Compliance Ops' to unlock Sanctions for Crypto Trading",
    ];
    deps.addNewsItem({
      id: nextNewsId(),
      text: alerts[Math.floor(Math.random() * alerts.length)],
      type: 'negative',
    });
  }

  if (nextTick === 5 && currentStats.budget < 1000 && currentResearch.completedTechs.length === 0) {
    deps.addNewsItem({
      id: nextNewsId(),
      text: '💡 NEW PLAYER TIP: Press R → Research to unlock new buildings!',
      type: 'neutral',
    });
  }

  const gridAfterTick = gameStore.getState().grid;
  const hasDiode = gridAfterTick
    .flat()
    .some((t) => t.buildingType === BuildingType.OneWayDiode && t.isConnected);

  gameStore.getState().setStats((prev) => {
    let newComp = prev.compliance + netComp;
    if (newComp > 100) newComp = 100;
    if (nextTick < 10 && newComp < 10) newComp = 10;

    let policyComplianceEffect = 0;
    let policyThreatEffect = 0;
    let policyBudgetEffect = 0;

    currentPolicyEffects.forEach((effect) => {
      policyComplianceEffect += effect.effects.compliance;
      policyThreatEffect += effect.effects.threat;
      policyBudgetEffect += effect.effects.budget;
    });

    newComp += policyComplianceEffect / 30;

    const threatResult = resolveEvaluateThreat({
      currentThreat: prev.threat,
      dailyThreatDelta: dailyThreat,
      policyThreatEffectTotal: policyThreatEffect,
      hasDiode,
      threatHistory: prev.threatHistory,
    });

    let newThreat = threatResult.threat;
    const newHistory = threatResult.threatHistory;

    if (threatResult.complianceDelta) {
      newComp += threatResult.complianceDelta;
    }

    if (threatResult.breachEvent === 'diode_mitigated') {
      deps.addNewsItem({
        id: nextNewsId(),
        text: 'DATA DIODE PREVENTED FULL EXFILTRATION.',
        type: 'positive',
      });
    } else if (threatResult.breachEvent === 'data_breach') {
      deps.addNewsItem({
        id: nextNewsId(),
        text: 'CRITICAL ALERT: DATA BREACH! Compliance plummeted.',
        type: 'negative',
      });
      if (threatResult.playAlertSfx) {
        resolvePlayAudio({ type: 'sfx', sound: 'alert' });
      }
    }

    if (newComp <= 0) {
      gameStore.getState().setGameStatus('lost');
      return { ...prev, compliance: 0 };
    }

    let newBudget = prev.budget + netBudget + policyBudgetEffect;
    let newLoanCount = prev.loanCount;

    if (newBudget < 0) {
      newBudget = 0;
      newLoanCount += 1;
      deps.addNewsItem({
        id: nextNewsId(),
        text: 'EMERGENCY LIQUIDITY TRIGGERED. Debt increased.',
        type: 'negative',
      });
      resolvePlayAudio({ type: 'sfx', sound: 'alert' });
    }

    if (newBudget >= WIN_BUDGET && !deps.activeScenario) {
      gameStore.getState().setGameStatus('won');
      resolvePlayAudio({ type: 'sfx', sound: 'success' });
    }

    return {
      ...prev,
      budget: newBudget,
      compliance: newComp,
      threat: newThreat,
      threatHistory: newHistory,
      loanCount: newLoanCount,
      day: prev.day + 1,
    };
  });

  const { activeScenario } = deps;
  let eventActive = deps.activeEvent != null;

  const scriptedResult = resolveTriggerScriptedEvent({
    nextTick,
    activeScenario,
    eventActive,
  });

  if (scriptedResult.triggered && scriptedResult.event) {
    eventActive = true;
    deps.setActiveEvent(scriptedResult.event);
    if (scriptedResult.playAlertSfx) {
      resolvePlayAudio({ type: 'sfx', sound: 'alert' });
    }
    if (scriptedResult.statAdjustments) {
      const adj = scriptedResult.statAdjustments;
      gameStore.getState().setStats((s) => ({
        ...s,
        compliance:
          adj.compliance != null
            ? Math.max(0, s.compliance + adj.compliance)
            : s.compliance,
        budget: adj.budget != null ? Math.max(0, s.budget + adj.budget) : s.budget,
        threat:
          adj.threat != null ? Math.min(100, s.threat + adj.threat) : s.threat,
      }));
    }
  }

  const randomResult = resolveTriggerRandomEvent({
    eventActive,
    compliance: currentStats.compliance,
    threat: currentStats.threat,
    loanCount: currentStats.loanCount,
  });

  if (randomResult.triggered && randomResult.event) {
    eventActive = true;
    deps.setActiveEvent(randomResult.event);
    if (randomResult.playAlertSfx) {
      resolvePlayAudio({ type: 'sfx', sound: 'alert' });
    }
    if (randomResult.statAdjustments) {
      const adj = randomResult.statAdjustments;
      gameStore.getState().setStats((s) => ({
        ...s,
        budget: adj.budget != null ? Math.max(0, s.budget + adj.budget) : s.budget,
        loanCount: adj.loanCount != null ? s.loanCount + adj.loanCount : s.loanCount,
      }));
    }
  }

  if (activeScenario) {
    deps.setScenarioObjectives((prevObjs) => {
      const objResult = architectureFeatures.useTrainingUseCases
        ? executeScenarioObjectives({
            objectives: prevObjs,
            stats: tickStartStats,
            buildingCounts,
            research: tickStartResearch,
            violationCount: vTiles.length,
          })
        : executeLegacyScenarioObjectives({
            objectives: prevObjs,
            stats: tickStartStats,
            buildingCounts,
            research: tickStartResearch,
            violationCount: vTiles.length,
          });
      objResult.newlyCompleted.forEach((desc) => {
        deps.addNewsItem({
          id: nextNewsId(),
          text: `OBJECTIVE COMPLETE: ${desc}`,
          type: 'positive',
        });
        resolvePlayAudio({ type: 'sfx', sound: 'success' });
        recordPlatformEventAuto({
          eventType: 'objective_complete',
          payload: {
            description: desc,
            scenarioId: activeScenario?.id ?? null,
            day: tickStartStats.day,
          },
        });
      });
      return objResult.objectives;
    });
  } else {
    gameStore.getState().setMilestones((prevMs) => {
      const msResult = architectureFeatures.useTrainingUseCases
        ? executeMilestoneCheck({
            milestones: prevMs,
            stats: tickStartStats,
            research: tickStartResearch,
            buildingCounts,
          })
        : executeLegacyMilestoneCheck({
            milestones: prevMs,
            stats: tickStartStats,
            research: tickStartResearch,
            buildingCounts,
          });
      msResult.newlyCompleted.forEach((ms) => {
        deps.addNewsItem({
          id: nextNewsId(),
          text: `MILESTONE: ${ms.name} - Reward $${ms.reward}`,
          type: 'positive',
        });
        resolvePlayAudio({ type: 'sfx', sound: 'success' });
        recordPlatformEventAuto({
          eventType: 'milestone_complete',
          payload: {
            milestoneId: ms.id,
            name: ms.name,
            reward: ms.reward,
            day: tickStartStats.day,
          },
        });
      });
      if (msResult.totalReward > 0) {
        gameStore.getState().setStats((s) => ({ ...s, budget: s.budget + msResult.totalReward }));
      }
      return msResult.milestones;
    });
  }

  deps.fetchNews();
}